import { FC, useContext, useEffect, useState } from "react";
import Blockly from "blockly/core";
import {
  BlocklyWorkspace,
  ToolboxDefinition,
  WorkspaceSvg,
} from "react-blockly";
import cn from "classnames";
// import Theme from "@blockly/theme-modern";
import { pythonGenerator } from "blockly/python";
import { IBlocklyProps } from "../types";
import workspaceConfiguration from "./workspaceConfiguration.json";
import { Screen } from "../helpers/Screen";
import { FileSystem } from "../helpers/FileSystem";
import { updateFlyOut, injectLibFromStack } from "./OverrideMsgs";

import ArduinoBlockly from "./generator";

import { scriptUrls } from "./constants";
import * as Tr from "blockly/msg/tr";
import { LanguageContext } from "../../../contexts/languageContext";

const urlParams = new URLSearchParams(window.location.search);
var ApiUrl = process.env.REACT_APP_CDN_URL;
if (window.location.hostname.includes("editor-staging.riders.ai")) {
  ApiUrl = process.env.REACT_APP_STAGING_CDN_URL;
}

if (
  window.location.hostname.includes("localhost") &&
  urlParams.has("localcdn")
) {
  ApiUrl = "/cdn";
}

export const BEditor: FC<IBlocklyProps> = (props) => {
  const { className, setCode, setXML, project, file_path } = props;

  const { refreshKey, theme } = useContext<any>(Screen.context);
  const { readFile } = useContext<any>(FileSystem.context);
  const { language } = useContext<any>(LanguageContext);

  const [userXML, setUserXML] = useState<string | undefined>(undefined);

  useEffect(() => {
    setUserXML(undefined);
    readFile(file_path).then((code: string) => {
      setUserXML(code);
    });
  }, [refreshKey]);

  const [toolboxCategories, setToolboxCategories] = useState<
    any | ToolboxDefinition
  >({
    kind: "categoryToolbox",
    contents: [],
  }); // Empty initial toolbox, if it will not find toolboxCategories.json

  const workspaceChange = (workspace: WorkspaceSvg, init?: boolean) => {
    window.getBlocklyWorkspace = (CB) => {
      CB(workspace);
    };

    var code = "";
    try {
      if (project?.dev === "blockly") {
        if (
          window.Blockly.Arduino === undefined ||
          window.Blockly === undefined
        ) {
          return;
        }
        code = window.Blockly.Arduino.workspaceToCode(workspace);
      } else {
        code = pythonGenerator.workspaceToCode(workspace);
      }

      updateFlyOut(Blockly, workspace);
    } catch (error) {
      console.log(error + "Error Code " + project?.dev);
    }

    if (init !== true) {
      setCode(code);
    }
    try {
      const _panel: SVGAElement | null = document.querySelector(
        "#blockly_wrapper > div > div > svg:nth-child(7)"
      );
      if (_panel && _panel.style.display === "none")
        (document.querySelector(
          "#blockly_wrapper > div > div > svg:nth-child(8)"
        ) as SVGAElement)!.style.transform = "";
    } catch (ignored) {}

    const scrollbarArea: any = document.getElementsByClassName(
      "blocklyFlyoutScrollbar"
    )[1];
    const flyout = document.getElementsByClassName("blocklyFlyout")[1];
    const flyoutScrollBar = document
      .getElementsByClassName("blocklyFlyoutScrollbar")[1]
      .querySelector(".blocklyScrollbarHandle");
    if (flyout && flyoutScrollBar) {
      if (
        parseFloat(flyout.getAttribute("height") as string) -
          parseFloat(flyoutScrollBar.getAttribute("height") as string) ===
        5
      ) {
        scrollbarArea.style.display = "none";
      } else {
        scrollbarArea.style.display = "block";
      }
    }
  };

  const onXmlChange = (newXML: string) => {
    setXML(newXML);
  };

  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    fetch(`/arduino-blockly/riders_toolbox.json`)
      .then((res) => res.json())
      .then((data) => {
        setToolboxCategories(data);
      });
  }, [project]);

  useEffect(() => {
    //console.log("fetchBlockData: ", language.toLowerCase());
    async function fetchBlockData(toolboxName: string) {
      try {
        window.Blockly = Blockly;
        window.Blockly.Arduino = ArduinoBlockly;

        injectLibFromStack(scriptUrls, "/arduino-blockly/");
        injectLibFromStack(
          [`lang/${language.toLowerCase()}.js`],
          "/arduino-blockly/"
        );

        setTimeout(setScriptLoaded, 1000, true);
      } catch (error) {
        //console.error(error);
      }
    }
    fetchBlockData(project.toolboxName);
  }, [project]);

  useEffect(() => {
    if (window.blocklyThemeLoaded) return;

    if (project.blocklyTheme === "zelos" && language !== "EN") {
      Blockly.setLocale(Tr);
    }

    Blockly.Theme.defineTheme("riders", {
      name: "riders",
      base: Blockly.Themes.Classic,

      componentStyles: {
        workspaceBackgroundColour: theme === "dark" ? "#2E3B4C" : "#fafafa",
        toolboxBackgroundColour: theme === "dark" ? "#2E3B4C" : "#fff",
        toolboxForegroundColour: "#000",
        flyoutBackgroundColour: "#ddd",
        flyoutForegroundColour: "#000",
        flyoutOpacity: 0.75,
        scrollbarColour: "#1e1e1e",
        scrollbarOpacity: 0.6,
      },

      fontStyle: {},
      startHats: false,
    });

    window.blocklyThemeLoaded = true;
  }, []);

  if (!scriptLoaded || !project) {
    return (
      <div className={className} id="blockly_wrapper">
        <div className="h-full flex flex-col justify-center items-center">
          <span className="mb-6 text-2xl">Loading Block Coding</span>
          <div style={{ display: "flex" }}>
            <div className="dot dot--one"></div>
            <div className="dot dot--two"></div>
            <div className="dot dot--three"></div>
            <div className="dot dot--four"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(className, "zelos-theme")}
      id="blockly_wrapper"
      key={refreshKey}
    >
      {userXML && (
        <BlocklyWorkspace
          className="h-full"
          toolboxConfiguration={toolboxCategories}
          workspaceConfiguration={{
            media: workspaceConfiguration.media,
            renderer: "zelos",
            zoom: {
              ...workspaceConfiguration.zoom,
              startScale: 0.75,
            },
            theme: "riders",
            grid: {
              spacing: 20,
              length: 3,
              colour: theme === "dark" ? "#fff" : "#ccc",
              snap: true,
            },
            move: {
              scrollbars: {
                horizontal: true,
                vertical: true,
              },
              drag: true,
              wheel: true,
            },
          }}
          initialXml={userXML}
          onInject={(w) => workspaceChange(w, true)}
          onWorkspaceChange={workspaceChange}
          onXmlChange={onXmlChange}
        />
      )}
    </div>
  );
};
